import { useMemo } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import "@fontsource/montserrat";
import { positions } from "@mui/system";

export const COLORS = {
  main: "#504F54",
  secondary: "#2D50FF",
  textMain: "#000000",
  textSecondary: "#67676C",
  textPrimary: "#252525",
  lightGrey: "#EBEAED",
  red: "#F45B54",
  bgHover: "#2949E8",
  borderFocus: "#728AFF",
  inputBorder: "#E4E8D3",
  background: "#FEFFF8",
  iconColor: "#67676C",
  success: "#1C8352",
  error: "#B53129",
  focusBorder: "#2949E8",
  focusBoxShadow: "#728AFF",
};

const MuiTheme: React.FC = ({ children }) => {
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: COLORS.main,
          },
          secondary: {
            main: COLORS.secondary,
          },
          text: {
            primary: COLORS.textMain,
            secondary: COLORS.textSecondary,
          },
          action: {
            active: COLORS.secondary,
          },
          background: {
            default: COLORS.background,
          },
        },
        typography: {
          fontFamily: '"Aeonik", sans-serif',
          htmlFontSize: 14,
          h1: {
            fontSize: 32,
            fontWeight: 400,
            paddingBottom: 12,
          },
          h2: {
            fontSize: 24,
            fontWeight: 400,
            paddingBottom: 16,
          },
          h3: {
            fontSize: 20,
            fontWeight: 400,
            paddingBottom: 24,
          },
          h4: {
            fontSize: 16,
            fontWeight: 600,
            paddingBottom: 12,
          },
          subtitle1: {
            fontSize: 14,
            paddingBottom: 24,
            color: COLORS.textSecondary,
          },
          subtitle2: {
            fontSize: 16,
            p: 0,
            color: COLORS.textMain,
          },
        },
        components: {
          MuiTextField: {
            styleOverrides: {
              root: {
                color: "#000",
                "input::-webkit-clear-button, input::-webkit-outer-spin-button, input::-webkit-inner-spin-button":
                {
                  display: "none",
                  margin: 80,
                },
                "input[type=number]": {
                  MozAppearance: "textfield",
                },
              },
            },
            variants: [
              {
                props: { variant: "standard" },
                style: {
                  "& .MuiInputBase-input": {
                    color: COLORS.textMain, // Text color
                  },
                  "& .MuiFormLabel-root": {
                    textOverflow: "ellipsis !important",
                    color: COLORS.textSecondary,
                  },
                  "& .MuiInput-underline:before": {
                    borderBottomColor: COLORS.lightGrey, // Underline
                  },
                  "& .MuiInput-underline:hover:before": {
                    borderBottomColor: COLORS.secondary, // Underline on hover (not working)
                    borderBottomWidth: 1,
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: COLORS.secondary, // Underline on focus
                    borderBottomWidth: 1,
                  },
                },
              },
              {
                props: { variant: "outlined" },
                style: {
                  "& .MuiInputBase-input": {
                    fontSize: 14,
                    paddingTop: 8,
                    paddingBottom: 8,
                    background: "#fff",
                  },
                },
              },
            ],
          },
          MuiFormControlLabel: {
            styleOverrides: {
              root: {
                color: COLORS.textSecondary,
                "& .MuiFormControlLabel-label": {
                  fontSize: 14,
                },
              },
            },
          },
          MuiLink: {
            styleOverrides: {
              root: {
                color: COLORS.secondary,
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 0,
                height: "40px",
                fontSize: 14,
                "& .MuiButton-icon": {
                  marginRight: "6px",
                  "& .MuiSvgIcon-root": {
                    marginRight: 0,
                  },
                },
                "&.Mui-disabled": {
                  backgroundColor: "#67676C",
                  color: "#C9C9D0",
                },
              },
              outlined: {
                paddingTop: 8,
                paddingBottom: 8,
                background: "#fff",
                border: "1px solid #E4E8D3",
                "&:hover, &:focus": {
                  background: "#fff",
                  borderColor: "#E4E8D3",
                },
              },
            },
          },
          MuiAutocomplete: {
            styleOverrides: {
              root: {
                "& .MuiOutlinedInput-root": {
                  background: "#fff",
                  borderRadius: "8px",
                  height: 40,
                  paddingRight: "12px",
                  paddingLeft: "12px",
                  paddingTop: "9px",
                  paddingBottom: "9px",
                  "& .MuiAutocomplete-input": {
                    padding: 0,
                  },
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              notchedOutline: {
                borderColor: COLORS.inputBorder,
              },
              root: {
                paddingRight: "12px",
                borderRadius: "8px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid",
                  borderColor: COLORS.focusBorder,
                  boxShadow: "0 0 0 2px " + COLORS.focusBoxShadow,
                },
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              root: {
                height: "40px",
                padding: 0,
                background: "#fff",
                borderRadius: "8px",
                fontSize: "14px",
                boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.04)",
              },
              input: {
                color: COLORS.textMain,
                "&:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 100px #fff inset",
                  WebkitTextFillColor: COLORS.textMain,
                  transition: "background-color 5000s ease-in-out 0s",
                },
              },
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: {
                color: COLORS.inputBorder,
                borderRadius: "8px",
                "&:hover": {
                  background: "inherit",
                },
                "&.Mui-checked": {
                  color: COLORS.inputBorder,
                },
                "&.Mui-disabled": {
                  opacity: "0.4",
                },
              },
            },
          },
          MuiTablePagination: {
            styleOverrides: {
              root: {
                "& .MuiTablePagination-displayedRows": {
                  fontSize: 14,
                },
                "& .MuiTablePagination-selectLabel": {
                  fontSize: 14,
                },
              },
              input: {
                width: "80px",
                "& .MuiSelect-select.MuiTablePagination-select.MuiInputBase-input.MuiSelect-standard": {
                  paddingRight: "24px",
                },
                "& .MuiSvgIcon-root": {
                  position: "absolute",
                  right: "7px",
                  color: COLORS.textSecondary,
                  top: "calc(50% - .5em)",
                  pointerEvents: "none",
                },
              },
            },
          },
          MuiPagination: {
            styleOverrides: {
              root: {
                fontSize: 14,
              },
            },
          },
          MuiAccordion: {
            styleOverrides: {
              root: {
                "&::before": {
                  height: 0,
                },
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              root: {
                "&.Mui-disabled": {
                  color: "#85858B",
                  backgroundColor: "#EAEAED",
                },
                "& .MuiSvgIcon-root": {
                  position: "absolute",
                  right: "7px",
                  color: COLORS.textSecondary,
                  top: "calc(50% - .5em)",
                  pointerEvents: "none",
                },
              },
            },
          },
          MuiFormHelperText: {
            styleOverrides: {
              root: {
                paddingLeft: 0,
                marginLeft: 0,
              },
            },
          },
          MuiAccordionDetails: {
            styleOverrides: {
              root: {
                padding: "24px",
              },
            },
          },
        },
      }),
    []
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default MuiTheme;
